import {
  ThreadListItemPrimitive,
  ThreadListPrimitive,
  useAssistantApi,
  useAssistantState,
  useThreadListItem,
  useThreadListItemRuntime
} from "@assistant-ui/react";
import { ArchiveIcon, MoreHorizontalIcon, PencilIcon, PlusIcon, TrashIcon } from "lucide-react";
import type { FC } from "react";
import React, { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

export const ThreadList: FC<{ onSelect?: () => void }> = ({ onSelect }) => {
  const threadIds = useAssistantState(s => s.threads.threadIds);
  const isLoading = useAssistantState(s => s.threads.isLoading);

  React.useEffect(() => {
    console.log('ThreadList: Rendering with threadIds:', threadIds);
    console.log('ThreadList: isLoading:', isLoading);
  }, [threadIds, isLoading]);

  return (
    <ThreadListPrimitive.Root className="aui-root aui-thread-list-root flex flex-col items-stretch gap-1.5">
      <ThreadListNew />
      <ThreadListItems onSelect={onSelect} />
    </ThreadListPrimitive.Root>
  );
};

const ThreadListNew: FC = () => {
  return (
    <ThreadListPrimitive.New asChild>
      <Button
        className="aui-thread-list-new flex items-center justify-start gap-1 rounded-lg px-2.5 py-2 text-start hover:bg-muted data-active:bg-muted"
        variant="ghost"
      >
        <PlusIcon />
        New Thread
      </Button>
    </ThreadListPrimitive.New>
  );
};

const ThreadListItems: FC<{ onSelect?: () => void }> = ({ onSelect }) => {
  return <ThreadListPrimitive.Items components={{ ThreadListItem: (props) => <ThreadListItem {...props} onSelect={onSelect} /> }} />;
};

const ThreadListItem: FC<any & { onSelect?: () => void }> = ({ onSelect, ...props }) => {
  return (
    <ThreadListItemPrimitive.Root {...props} className="aui-thread-list-item group flex items-center gap-2 rounded-lg transition-all hover:bg-muted focus-visible:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none data-[active]:bg-muted">
      <ThreadListItemPrimitive.Trigger className="aui-thread-list-item-trigger flex-grow px-3 py-2 text-start" onClick={onSelect}>
        <ThreadListItemTitle />
      </ThreadListItemPrimitive.Trigger>
      <ThreadListMore />
    </ThreadListItemPrimitive.Root>
  );
};

const ThreadListItemTitle: FC = () => {
  return (
    <span className="aui-thread-list-item-title text-sm line-clamp-1">
      <ThreadListItemPrimitive.Title fallback="New Chat" />
    </span>
  );
};

const ThreadListMore: FC = () => {
  const [renameOpen, setRenameOpen] = useState(false);

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            className="aui-thread-list-item-more mr-2 ml-auto size-6 p-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100 hover:text-foreground"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <MoreHorizontalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={(e) => {
            e.stopPropagation();
            setRenameOpen(true);
          }}>
            <PencilIcon className="mr-2 size-4" />
            Rename
          </DropdownMenuItem>

          <ThreadListItemPrimitive.Archive asChild>
            <DropdownMenuItem onClick={(e) => e.stopPropagation()}>
              <ArchiveIcon className="mr-2 size-4" />
              Archive
            </DropdownMenuItem>
          </ThreadListItemPrimitive.Archive>

          {/* FIX: Wrap the delete item in ThreadListItemPrimitive.Delete */}
          <ThreadListItemPrimitive.Delete asChild>
            <DropdownMenuItem
              className="text-destructive focus:bg-destructive focus:text-destructive-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <TrashIcon className="mr-2 size-4" />
              Delete
            </DropdownMenuItem>
          </ThreadListItemPrimitive.Delete>
        </DropdownMenuContent>
      </DropdownMenu>

      {renameOpen && <RenameThreadDialog open={renameOpen} onOpenChange={setRenameOpen} />}
    </>
  );
};

const RenameThreadDialog: FC<{ open: boolean; onOpenChange: (open: boolean) => void }> = ({
  open,
  onOpenChange,
}) => {
  const thread = useThreadListItem();
  const runtime = useThreadListItemRuntime();
  const [name, setName] = useState(thread.title || "");

  const handleSave = async () => {
    try {
      await runtime.rename(name);
      console.log("Renamed thread to", name);
    } catch (error) {
      console.error("Error renaming thread:", error);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Rename Thread</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Thread Name"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={(e) => {
            e.stopPropagation();
            onOpenChange(false);
          }}>
            Cancel
          </Button>
          <Button onClick={(e) => {
            e.stopPropagation();
            handleSave();
          }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
